import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Card,
  CardContent,
  Typography,
  DialogActions,
  Button,
  CircularProgress,
  FormControl,
  Select
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sortAlphabetically } from "../../utils/sortAlphabetically";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function UnitLocationFields({ disabled = false, darkTheme = false, device, onDeviceUpdated }) {
  const [allGroups, setAllGroups] = useState([]);
  const [allRegions, setAllRegions] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("");

  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);

  const [originalGroup, setOriginalGroup] = useState("");
  const [originalRegion, setOriginalRegion] = useState("");
  const [originalRoute, setOriginalRoute] = useState("");

  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch groups
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_GATEAWAY}/groups`, {
          params: { companyId: process.env.REACT_APP_COMPANY_ID, type: "geographical" },
        });
        const filteredGroups = response.data.filter(
          (group) => !group.name?.toLowerCase().includes("pytro group")
        );
        const sortedData = sortAlphabetically(filteredGroups, 'name');
        setAllGroups(sortedData);
      } catch (err) {
        console.error("Error fetching groups:", err);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  // Fetch regions
  useEffect(() => {
    if (!selectedGroup) {
      setAllRegions([]);
      setSelectedRegion("");
      return;
    }
    const fetchRegions = async () => {
      setLoadingRegions(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/regions?groupId=${selectedGroup}`,
          { params: { companyId: process.env.REACT_APP_COMPANY_ID } }
        );
        setAllRegions(response.data);
      } catch (err) {
        console.error("Error fetching regions:", err);
        setAllRegions([]);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, [selectedGroup]);

  // Fetch routes
  useEffect(() => {
    if (!selectedRegion) {
      setAllRoutes([]);
      setSelectedRoute("");
      return;
    }
    const fetchRoutes = async () => {
      setLoadingRoutes(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/routes?regionId=${selectedRegion}`
        );
        setAllRoutes(response.data);
      } catch (err) {
        console.error("Error fetching routes:", err);
        setAllRoutes([]);
      } finally {
        setLoadingRoutes(false);
      }
    };
    fetchRoutes();
  }, [selectedRegion]);

  // Initialize selects from device
  useEffect(() => {
    if (!device?.assets) return;
    const geoAsset = device.assets.find((asset) => asset.group?.type === "geographical");
    if (geoAsset) {
      setSelectedGroup(geoAsset.group?.id || "");
      setSelectedRegion(geoAsset.region?.id || "");
      setSelectedRoute(geoAsset.route?.id || "");
      setOriginalGroup(geoAsset.group?.id || "");
      setOriginalRegion(geoAsset.region?.id || "");
      setOriginalRoute(geoAsset.route?.id || "");
    }
  }, [device]);

  // Update handler
  const handleUpdate = async () => {
    if (!device?.id) return;

    setIsUpdating(true);
    try {
      const normalize = (val) => (val === "" ? null : val);

      const noChange =
        normalize(selectedGroup) === normalize(originalGroup) &&
        normalize(selectedRegion) === normalize(originalRegion) &&
        normalize(selectedRoute) === normalize(originalRoute);

      if (noChange) {
        toast.info("No changes detected.");
        return;
      }

      const payload = {
        newAssets:
          selectedGroup || selectedRegion || selectedRoute
            ? [
              {
                groupId: normalize(selectedGroup),
                regionId: normalize(selectedRegion),
                routeId: normalize(selectedRoute),
              },
            ]
            : [], // ✅ empty array if nothing selected
        oldAssets: originalGroup
          ? [
            {
              groupId: normalize(originalGroup),
              regionId: normalize(originalRegion),
              routeId: normalize(originalRoute),
            },
          ]
          : [],
        UI_Description: `${getUIDescription()}\n "Update Button of the Select Group ,Region & Route"`, 
      };

      await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?id=${device.id}`,
        payload
      );

      setOriginalGroup(selectedGroup);
      setOriginalRegion(selectedRegion);
      setOriginalRoute(selectedRoute);

      toast.success("✅ Group updated successfully!");
      // const YARD_GROUP_ID = "GROUP-2020-12-06-16-43-12-629207";


      onDeviceUpdated();

    } catch (error) {
      console.error("Error updating group:", error);
      toast.error("Failed to update group. Please try again!");
    } finally {
      setIsUpdating(false);
    }
  };

  // Styling
  const textFieldSx = darkTheme
    ? {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#374151",
        color: "#fff",
        "& fieldset": { borderColor: "#4b5563" },
        "&:hover fieldset": { borderColor: "#6b7280" },
        "&.Mui-focused fieldset": { borderColor: "#ea81ee" },
      },
      "& .MuiInputLabel-root": { color: "#9ca3af" },
      "& .MuiInputLabel-root.Mui-focused": { color: "#ea81ee" },
      "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "#9ca3af" },
    }
    : {};

  const menuPropsSx = darkTheme
    ? {
      PaperProps: {
        sx: {
          backgroundColor: "#374151",
          color: "#fff",
          "& .MuiMenuItem-root": {
            "&:hover": { backgroundColor: "#4b5563" },
            "&.Mui-selected": {
              backgroundColor: "#ea81ee",
              "&:hover": { backgroundColor: "#d946ef" },
            },
          },
        },
      },
    }
    : {};



  return (
    <Box sx={{ position: "relative" }}>
      {loadingGroups ? (
        <Box
          sx={{
            height: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      ) : (
        <Card
          sx={{
            backgroundColor: "#1E293B",
            border: "1px solid #2E3B4C",
            color: "#fff",
            borderRadius: 3,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Select Group, Region & Route
          </Typography>

          {/* Parent Group */}
          <Box>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Parent Group
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
              <Select
                value={selectedGroup || ""}
                onChange={(e) => {
                  const g = e.target.value;
                  setSelectedGroup(g);
                  setSelectedRegion("");
                  setSelectedRoute("");
                }}
                disabled={disabled || loadingGroups}
                sx={{
                  backgroundColor: "#2E3B4C",
                  color: "#fff",
                  border: "1px solid #60a5fa",
                  borderRadius: 1,
                  "& .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                <MenuItem value="">Select Group</MenuItem>
                {allGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Region */}
          <Box>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Region / District
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
              <Select
                value={selectedRegion || ""}
                onChange={(e) => {
                  const r = e.target.value;
                  setSelectedRegion(r);
                  setSelectedRoute("");
                }}
                disabled={disabled || loadingRegions}
                sx={{
                  backgroundColor: "#2E3B4C",
                  color: "#fff",
                  border: "1px solid #60a5fa",
                  borderRadius: 1,
                  "& .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                <MenuItem value="">No Region selected </MenuItem>
                {allRegions.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Route */}
          <Box>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Technician
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
              <Select
                value={selectedRoute || ""}
                onChange={(e) => setSelectedRoute(e.target.value)}
                disabled={disabled || loadingRoutes}
                sx={{
                  backgroundColor: "#2E3B4C",
                  color: "#fff",
                  border: "1px solid #60a5fa",
                  borderRadius: 1,
                  "& .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                <MenuItem value="">No Technician Selected</MenuItem>
                {allRoutes.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name || `${r.firstName} ${r.lastName}` || r.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Update Button */}
          <Button
            onClick={handleUpdate}
            disabled={isUpdating}
            sx={{
              mt: 2,
              backgroundColor: "#60a5fa",
              color: "#000",
              fontWeight: 600,
              borderRadius: 1,
              textTransform: "none",
              "&:hover": { backgroundColor: "#3b82f6" },
              "&:disabled": { backgroundColor: "#334155", color: "#94a3b8" },
            }}
          >
            {isUpdating ? "Updating..." : "Update"}
          </Button>
        </Card>
      )}

      {/* Overlay loading spinner */}
      {isUpdating && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            zIndex: 5,
          }}
        >
          <CircularProgress sx={{ color: "#60a5fa", mb: 2 }} />
          <Typography variant="body2" fontWeight={500}>
            Updating unit...
          </Typography>
        </Box>
      )}
    </Box>
  );
}